/**
 * A RELOAD INSIDE AN OPEN SCREEN (the loading rule, 2): switching Week to Month, a tab, a filter, a
 * refresh. The card keeps its size and shows grey blocks shaped like its own content, a figure row
 * and a chart area, in the console's one shimmer (`.lm-sk`), and only once the wait has passed a
 * quarter of a second (`useDelayedPending`), so a fast reload never flashes.
 */
import type { ReactNode } from 'react';
import { useDelayedPending } from './use-delayed-pending';

/**
 * Grey blocks in a card's own shape. `figures` stat blocks across the top, a `chart` area of that
 * height, `rows` list lines under it; any of them left out is not drawn.
 */
export function CardPlaceholder({
  figures = 0,
  chart = 0,
  rows = 0,
  className = '',
}: {
  figures?: number;
  /** The chart area's height: px, or a CSS length where the card's own is one. */
  chart?: number | string;
  rows?: number;
  className?: string;
}): ReactNode {
  return (
    <div className={`flex w-full flex-col gap-[10px] ${className}`} aria-busy="true" aria-label="Reading">
      {figures > 0 && (
        <div className="flex flex-wrap gap-[10px]">
          {Array.from({ length: figures }, (_, i) => (
            <span key={i} className="lm-sk inline-block h-[38px] w-[96px] rounded-[var(--radius-sm)]" />
          ))}
        </div>
      )}
      {(typeof chart === 'string' || chart > 0) && (
        /* Runtime size: the card's own chart height, so nothing jumps when the chart lands. */
        <span className="lm-sk block w-full rounded-[var(--radius-sm)]" style={{ height: chart }} />
      )}
      {rows > 0 &&
        Array.from({ length: rows }, (_, i) => <span key={i} className="lm-sk block h-[14px] w-full rounded-[4px]" />)}
    </div>
  );
}

/**
 * A `CardPlaceholder` for something reloading now: it holds its size from the start, so nothing jumps,
 * and shows its blocks only once the wait passes a quarter of a second (`useDelayedPending`). Render
 * it only while the reload is pending.
 */
export function ReloadPlaceholder(props: { figures?: number; chart?: number | string; rows?: number; className?: string }): ReactNode {
  const shown = useDelayedPending(true);
  return <CardPlaceholder {...props} className={`${props.className ?? ''} ${shown ? '' : 'invisible'}`} />;
}
